import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  configApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { AzureResourceQueryItem } from '@internal/backstage-plugin-bidone-backend';
import { UseQueryCustomResult } from '../types';
import { fetchAzureQueryResources } from './useQueryAzure';
import {
  AZURE_TAG_GIT_TAG_OR_BRANCH,
  PERMA_TAG_DEPLOYMENTGROUP,
  PERMA_TAG_ENVIRONMENT,
  PERMA_TAG_REGION,
  PERMA_TAG_REPO,
  DEPLOYMENT_GROUPS,
  customComponentMap,
} from '../components/VersionChecker/types';

/**
 * Extract the repo name from a build/repository URL (or a workspace/repo id).
 * Example: `https://bitbucket.org/bidone/dataloader.git` → `'dataloader'`.
 * Used to look up `Record<repoName, group>` from build data.
 */
export const repoNameFromUrl = (input: string | undefined): string => {
  if (!input) return '';
  const cleaned = input.replace(/\.git$/i, '').replace(/\/+$/, '');
  const parts = cleaned.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Returns a map from lowercased Bitbucket repo name to its deployment group.
 *
 * Sources merged together:
 *   1. Azure resources tagged with `PermaTagDeploymentGroup` (one query per
 *      known group, in parallel). `tags[PermaTagGitRepo]` is the key.
 *   2. `customComponentMap` in `VersionChecker/types.ts` — covers repos that
 *      aren't deployed to Azure (e.g. MFEs as static content, database
 *      migration repos). Inner values are repo names.
 *
 * The helper is intentionally **catalog-free** so DB repos (which aren't
 * registered in Backstage) still resolve.
 */
export const useRepoDeploymentGroups = (): UseQueryCustomResult<
  Record<string, string>
> => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const azureQueries = useQueries({
    queries: DEPLOYMENT_GROUPS.map(group => ({
      // Share the queryKey shape with `useAzureQueryResources` so VersionChecker's
      // cache is a hit for this lookup.
      queryKey: [
        'useQueryAzure',
        'useAzureQueryResources',
        `${PERMA_TAG_DEPLOYMENTGROUP}_${group}`,
      ] as const,
      staleTime: 15 * 60 * 1000,
      queryFn: async () => {
        const { token } = await identityApi.getCredentials();
        const msToken = await oauthApi.getAccessToken(
          'https://management.core.windows.net/.default',
        );
        return fetchAzureQueryResources(
          backendUrl,
          { [PERMA_TAG_DEPLOYMENTGROUP]: group },
          token,
          msToken,
        );
      },
    })),
  });

  const isFetching = azureQueries.some(q => q.isFetching);
  const error =
    (azureQueries.find(q => q.error)?.error as Error | undefined) ?? null;

  const data = useMemo<Record<string, string> | undefined>(() => {
    // Wait for at least some queries to settle before building the map.
    if (azureQueries.every(q => !q.data && q.isFetching)) return undefined;

    const map: Record<string, string> = {};

    // Azure pass — repos tagged with both PermaTagDeploymentGroup and
    // PermaTagGitRepo. First-write-wins so identical repos across multiple
    // resources don't fight (they should agree anyway).
    azureQueries.forEach((query, idx) => {
      const group = DEPLOYMENT_GROUPS[idx];
      const resources = (query.data ?? []) as AzureResourceQueryItem[];
      for (const resource of resources) {
        const repo =
          resource.tags?.[PERMA_TAG_REPO] || resource.tags?.['GitRepo'];
        if (!repo) continue;
        const key = repo.toLowerCase();
        if (!map[key]) {
          map[key] = group;
        }
      }
    });

    // Custom map pass — covers repos that aren't deployed to Azure (and so
    // never carry the PermaTag tags). Doesn't require a catalog entry.
    for (const [group, repoMap] of Object.entries(customComponentMap)) {
      for (const repoName of Object.values(repoMap)) {
        const key = repoName.toLowerCase();
        if (!map[key]) {
          map[key] = group;
        }
      }
    }

    return map;
  }, [azureQueries]);

  return {
    data,
    isFetching,
    error,
  };
};

/**
 * Returns, per repo, the `GitTagOrBranch` value currently tagged on each
 * Azure resource — i.e. the latest tag actually deployed per region.
 * Shape: `Record<repoName, Record<region, latestTag>>` (all lowercased).
 *
 * When `env` is provided, only resources tagged `PermaTagEnvironment=<env>`
 * contribute — important because the same repo typically has resources in
 * multiple envs (dev/qa/uat/prod), each with their own `GitTagOrBranch`
 * value, and the caller almost always wants a single env's view.
 *
 * Reuses the same set of Azure Resource Graph queries as
 * `useRepoDeploymentGroups` (matching queryKey shape), so React Query
 * dedupes — no extra network round-trips when both hooks are active.
 */
export const useLatestRepoTags = (
  env?: string,
): UseQueryCustomResult<Record<string, Record<string, string>>> => {
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const azureQueries = useQueries({
    queries: DEPLOYMENT_GROUPS.map(group => ({
      queryKey: [
        'useQueryAzure',
        'useAzureQueryResources',
        `${PERMA_TAG_DEPLOYMENTGROUP}_${group}`,
      ] as const,
      staleTime: 15 * 60 * 1000,
      queryFn: async () => {
        const { token } = await identityApi.getCredentials();
        const msToken = await oauthApi.getAccessToken(
          'https://management.core.windows.net/.default',
        );
        return fetchAzureQueryResources(
          backendUrl,
          { [PERMA_TAG_DEPLOYMENTGROUP]: group },
          token,
          msToken,
        );
      },
    })),
  });

  const isFetching = azureQueries.some(q => q.isFetching);
  const error =
    (azureQueries.find(q => q.error)?.error as Error | undefined) ?? null;

  const data = useMemo<Record<string, Record<string, string>> | undefined>(
    () => {
      if (azureQueries.every(q => !q.data && q.isFetching)) return undefined;

      const lowerEnv = env?.toLowerCase();
      const map: Record<string, Record<string, string>> = {};
      for (const query of azureQueries) {
        const resources = (query.data ?? []) as AzureResourceQueryItem[];
        for (const resource of resources) {
          if (lowerEnv) {
            const resourceEnv = (
              resource.tags?.[PERMA_TAG_ENVIRONMENT] || ''
            ).toLowerCase();
            if (resourceEnv !== lowerEnv) continue;
          }
          const repo = (
            resource.tags?.[PERMA_TAG_REPO] ||
            resource.tags?.['GitRepo'] ||
            ''
          ).toLowerCase();
          const region = (
            resource.tags?.[PERMA_TAG_REGION] || ''
          ).toLowerCase();
          const tag =
            resource.tags?.[AZURE_TAG_GIT_TAG_OR_BRANCH] ||
            resource.tags?.['gitTagOrBranch'] ||
            '';
          if (!repo || !region || !tag) continue;
          if (!map[repo]) map[repo] = {};
          // Last-write-wins; with env scoped, same repo+region resources in
          // the same env should agree on the tag.
          map[repo][region] = tag;
        }
      }
      return map;
    },
    [azureQueries, env],
  );

  return {
    data,
    isFetching,
    error,
  };
};
